import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { useMap } from "react-leaflet";
import { Search, X } from "lucide-react";
import { searchTasks } from "../../service/Task";
import { isTaskVisibleForGroupAccess } from "../task/GroupAssignParts";
import translations from "../common/Translations";
import { toast } from "../../utils/toast";
import Tooltip from "../common/Tooltip";

const SEARCH_RESULT_SIZE = 50;
const SINGLE_LOCATION_ZOOM = 17;

// The search API names assignees `assigneeName`/`assigneeId`; the map's detail panel reads `name`/`userId`.
const withMapAssigneeFields = (task) => ({
  ...task,
  assignees: (task.assignees || []).map((a) => ({
    ...a,
    name: a.name ?? a.assigneeName,
    userId: a.userId ?? a.assigneeId,
  })),
});

const toLatLng = (task) => {
  const lat = Number(task?.latitude);
  const lng = Number(task?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  if (lat === 0 && lng === 0) return null;
  return [lat, lng];
};

// Account-number search: shows whatever the search API returns for the activity, whether or not
// the pins are loaded on the map (map filters and the map's task limit don't apply).
const MapAccountSearch = ({ activityId, groupAllowedAgentIds, langMode, onHighlight, onResult, onClear }) => {
  const map = useMap();
  const t = translations[langMode] || translations.en;
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasResult, setHasResult] = useState(false);
  const containerRef = useRef(null);
  const abortRef = useRef(null);

  // Keep typing and clicks in the box from dragging or zooming the map.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    L.DomEvent.disableClickPropagation(el);
    L.DomEvent.disableScrollPropagation(el);
  }, []);

  useEffect(() => () => abortRef.current?.abort(), []);

  // A new activity invalidates the previous result.
  useEffect(() => {
    abortRef.current?.abort();
    setQuery("");
    setHasResult(false);
    setLoading(false);
    onHighlight?.([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityId]);

  const clearSearch = () => {
    abortRef.current?.abort();
    onHighlight?.([]);
    setQuery("");
    setLoading(false);
    if (hasResult) onClear?.();
    setHasResult(false);
  };

  const handleSearch = async () => {
    const accountNo = query.trim();
    if (!accountNo) return;
    if (activityId == null || activityId === "") {
      toast.warn(t.selectWorkflowFirst ?? "Select activity first.");
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);

    try {
      const workflowIds = String(activityId).split(",").map((id) => id.trim()).filter(Boolean);
      const { taskList } = await searchTasks({
        workflowIds,
        page: 0,
        size: SEARCH_RESULT_SIZE,
        provideTasksWithMissingLatAndLong: false,
        columnFilters: [{ column: "accountNo", op: "in", values: [accountNo] }],
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;

      const visible = (taskList || []).filter((task) => isTaskVisibleForGroupAccess(task, groupAllowedAgentIds));
      onHighlight?.([]);

      if (!visible.length) {
        setHasResult(false);
        toast.error(t.mapAccountNotFound ?? "Account not found");
        return;
      }

      const located = visible.filter((task) => toLatLng(task)).map(withMapAssigneeFields);
      if (!located.length) {
        setHasResult(false);
        toast.warn(t.mapAccountLocationUnavailable ?? "Location not available for this account");
        return;
      }

      // The cluster layer zooms when every pin is already on the map; otherwise zoom here.
      if (!onHighlight?.(located)) {
        const bounds = L.latLngBounds(located.map(toLatLng));
        if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
          map.flyTo(bounds.getCenter(), SINGLE_LOCATION_ZOOM);
        } else {
          map.flyToBounds(bounds, { padding: [60, 60], maxZoom: SINGLE_LOCATION_ZOOM });
        }
      }

      setHasResult(true);
      onResult?.(located);
    } catch (error) {
      if (controller.signal.aborted || error?.name === "CanceledError" || error?.code === "ERR_CANCELED") return;
      const message = error?.response?.data?.body?.value ?? error?.response?.data?.body?.error ?? error?.message;
      if (message) toast.error(message);
    } finally {
      if (abortRef.current === controller) setLoading(false);
    }
  };

  const placeholder = t.mapAccountSearchPlaceholder ?? "Search account number";

  return (
    <div
      ref={containerRef}
      className="flex h-8 lg:h-10 w-[210px] lg:w-[250px] items-center gap-1 rounded-xl border border-gray-200 bg-white ps-3 pe-1 shadow-md focus-within:border-[#236DB4] max-sm:h-9 max-sm:w-full"
      data-testid="map-account-search"
    >
      <input
        type="text"
        inputMode="numeric"
        value={query}
        onChange={(e) => setQuery(e.target.value.replace(/\D/g, ""))}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleSearch();
          if (e.key === "Escape") clearSearch();
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        className="min-w-0 flex-1 border-0 bg-transparent p-0 text-xs lg:text-sm text-slate-700 outline-none focus:ring-0"
      />
      {(query || hasResult) && (
        <button
          type="button"
          onClick={clearSearch}
          aria-label={t.clear ?? "Clear"}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        >
          <X size={14} />
        </button>
      )}
      <Tooltip text={t.search ?? "Search"} position="bottom">
        <button
          type="button"
          onClick={handleSearch}
          disabled={loading || !query.trim()}
          aria-label={t.search ?? "Search"}
          data-testid="map-account-search-button"
          className="flex h-6 w-7 lg:h-8 lg:w-9 shrink-0 items-center justify-center rounded-lg bg-[#236DB4] text-white transition-colors hover:bg-[#1d5a96] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? (
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          ) : (
            <Search size={15} />
          )}
        </button>
      </Tooltip>
    </div>
  );
};

export default MapAccountSearch;
